/**
 * EquityChart — live-moving portfolio equity terminal.
 *
 * • Server sync every 15 s (equity + orders + boost history) via /api/terminal/*
 * • 500 ms tick simulator so the line + benchmark visibly ripple between fetches
 * • Timeframe toggle: 1H · 24H · 7D · ALL — filters the visible window
 * • Trade markers: ▲ buy · ▼ sell · ★ boost overlaid on the curve
 * • Benchmark overlay: faint BTC line normalised to the same span so clients
 *   can see whether their curve beats the market
 */
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Activity } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TICK_MS = 500;
const FETCH_MS = 15000;

const TIMEFRAMES = [
  { k: "1h",  l: "1H",  seconds: 60 * 60,           btcSpan: "1h" },
  { k: "24h", l: "24H", seconds: 24 * 60 * 60,      btcSpan: "1d" },
  { k: "7d",  l: "7D",  seconds: 7 * 24 * 60 * 60,  btcSpan: "1w" },
  { k: "all", l: "ALL", seconds: Infinity,          btcSpan: "1y" },
];

const fmt = (n, d = 2) =>
  Number(n ?? 0).toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });

export default function EquityChart({ token, equity: liveEquity, dayPnl }) {
  const [tf, setTf] = useState("24h");
  const [buffer, setBuffer] = useState([]); // [tsSec, equity][] rolling — full history
  const [orders, setOrders] = useState([]);
  const [boosts, setBoosts] = useState([]);
  const [btcSeries, setBtcSeries] = useState([]); // [tsSec, price][]
  const [showCompare, setShowCompare] = useState(true);
  const [flash, setFlash] = useState(null);
  const anchorRef = useRef(null);
  const btcAnchorRef = useRef(null);
  const lastEquityRef = useRef(null);

  const activeTf = TIMEFRAMES.find((t) => t.k === tf) || TIMEFRAMES[1];

  // ── Server sync every 15 s ─────────────────────────────────────────────
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const headers = { Authorization: `Bearer ${token}` };
    const load = async () => {
      try {
        const [eq, ord, bh] = await Promise.all([
          axios.get(`${API}/terminal/equity`, { headers }),
          axios.get(`${API}/terminal/orders`, { headers }).catch(() => ({ data: { orders: [] } })),
          axios.get(`${API}/terminal/boost-history`, { headers }).catch(() => ({ data: { history: [] } })),
        ]);
        if (cancelled) return;
        const raw = eq.data.points || [];
        if (raw.length) {
          anchorRef.current = raw[raw.length - 1][1];
          setBuffer(raw);
        }
        setOrders(ord.data.orders || []);
        setBoosts(bh.data.history || []);
      } catch { /* silent */ }
    };
    load();
    const id = setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, FETCH_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [token]);

  // ── Live equity from parent — replaces the tail anchor ─────────────────
  useEffect(() => {
    if (liveEquity == null) return;
    anchorRef.current = Number(liveEquity);
    setBuffer((prev) => {
      const now = Date.now() / 1000;
      if (!prev.length) return [[now, Number(liveEquity)]];
      const next = prev.slice();
      next[next.length - 1] = [next[next.length - 1][0], Number(liveEquity)];
      return next;
    });
  }, [liveEquity]);

  // ── BTC benchmark fetch (mirrors timeframe) — refreshes every 30 s ─────
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/markets/chart/BTC?span=${activeTf.btcSpan}`);
        if (cancelled) return;
        const series = (data?.candles || []).map((c) => [Math.floor(c[0] / 1000), c[4]]);
        if (series.length) {
          btcAnchorRef.current = series[series.length - 1][1];
          setBtcSeries(series);
        }
      } catch { /* silent */ }
    };
    load();
    const id = setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [activeTf.btcSpan]);

  // ── 500 ms tick simulator on the equity buffer + BTC benchmark ─────────
  useEffect(() => {
    const id = setInterval(() => {
      setBuffer((prev) => {
        const anchor = anchorRef.current;
        if (anchor == null || !prev.length) return prev;
        const [lastTs, lastVal] = prev[prev.length - 1];
        const drift = (Math.random() - 0.5) * anchor * 0.0006;
        let nextVal = lastVal + drift;
        const maxDev = anchor * 0.0015;
        if (nextVal > anchor + maxDev) nextVal = anchor + maxDev;
        if (nextVal < anchor - maxDev) nextVal = anchor - maxDev;
        nextVal = Math.round(nextVal * 100) / 100;
        if (lastEquityRef.current != null) {
          if (nextVal > lastEquityRef.current) setFlash("up");
          else if (nextVal < lastEquityRef.current) setFlash("down");
          setTimeout(() => setFlash(null), 220);
        }
        lastEquityRef.current = nextVal;
        return [...prev, [lastTs + TICK_MS / 1000, nextVal]].slice(-2000);
      });
      setBtcSeries((prev) => {
        const anchor = btcAnchorRef.current;
        if (anchor == null || !prev.length) return prev;
        const [lastTs, lastVal] = prev[prev.length - 1];
        const drift = (Math.random() - 0.5) * anchor * 0.0005;
        let nextVal = lastVal + drift;
        const maxDev = anchor * 0.002;
        if (nextVal > anchor + maxDev) nextVal = anchor + maxDev;
        if (nextVal < anchor - maxDev) nextVal = anchor - maxDev;
        return [...prev, [lastTs + TICK_MS / 1000, nextVal]].slice(-2000);
      });
    }, TICK_MS);
    return () => clearInterval(id);
  }, []);

  // ── Timeframe filter ────────────────────────────────────────────────────
  const visibleEquity = useMemo(() => {
    if (!buffer.length) return [];
    if (activeTf.seconds === Infinity) return buffer;
    const cutoff = buffer[buffer.length - 1][0] - activeTf.seconds;
    const filtered = buffer.filter((p) => p[0] >= cutoff);
    return filtered.length >= 2 ? filtered : buffer.slice(-2);
  }, [buffer, activeTf.seconds]);

  const visibleBtc = useMemo(() => {
    if (!btcSeries.length) return [];
    if (activeTf.seconds === Infinity) return btcSeries;
    const cutoff = btcSeries[btcSeries.length - 1][0] - activeTf.seconds;
    return btcSeries.filter((p) => p[0] >= cutoff);
  }, [btcSeries, activeTf.seconds]);

  const stats = useMemo(() => {
    if (visibleEquity.length < 2) return null;
    const vals = visibleEquity.map((p) => p[1]);
    const first = vals[0], last = vals[vals.length - 1];
    const min = Math.min(...vals), max = Math.max(...vals);
    const pnl = last - first, pct = first ? (pnl / first) * 100 : 0;
    const drawdown = max ? ((max - last) / max) * 100 : 0;
    return { first, last, min, max, pnl, pct, drawdown, up: pnl >= 0 };
  }, [visibleEquity]);

  // ── Path builders (equity, BTC benchmark, markers) ─────────────────────
  const W = 800, H = 200, PAD_T = 12, PAD_B = 26;

  const eqGeom = useMemo(() => {
    if (visibleEquity.length < 2 || !stats) return null;
    const tsMin = visibleEquity[0][0];
    const tsMax = visibleEquity[visibleEquity.length - 1][0];
    const tsRange = Math.max(tsMax - tsMin, 1);
    const vRange = Math.max(stats.max - stats.min, 1);
    const toX = (ts) => ((ts - tsMin) / tsRange) * W;
    const toY = (v) => H - PAD_B - ((v - stats.min) / vRange) * (H - PAD_T - PAD_B);
    const d = visibleEquity.map((p, i) => `${i ? "L" : "M"}${toX(p[0]).toFixed(2)} ${toY(p[1]).toFixed(2)}`).join(" ");
    const [lx, ly] = [toX(tsMax), toY(visibleEquity[visibleEquity.length - 1][1])];
    return { d, toX, toY, tsMin, tsMax, lx, ly };
  }, [visibleEquity, stats]);

  const btcGeom = useMemo(() => {
    if (!eqGeom || visibleBtc.length < 2) return null;
    const vals = visibleBtc.map((p) => p[1]);
    const btcMin = Math.min(...vals), btcMax = Math.max(...vals);
    const btcRange = Math.max(btcMax - btcMin, 1);
    // Map BTC onto the same X-range as equity (both fill the chart width) so
    // shape / performance can be compared even when timestamps don't overlap.
    const n = visibleBtc.length;
    const toY = (v) => H - PAD_B - ((v - btcMin) / btcRange) * (H - PAD_T - PAD_B);
    const d = visibleBtc.map((p, i) => {
      const x = (i / (n - 1)) * W;
      return `${i ? "L" : "M"}${x.toFixed(2)} ${toY(p[1]).toFixed(2)}`;
    }).join(" ");
    const btcFirst = visibleBtc[0][1], btcLast = visibleBtc[n - 1][1];
    const btcPct = btcFirst ? ((btcLast - btcFirst) / btcFirst) * 100 : 0;
    return { d, btcPct };
  }, [eqGeom, visibleBtc]);

  const markers = useMemo(() => {
    if (!eqGeom) return [];
    const out = [];
    const fromDate = (v) => (typeof v === "string" ? new Date(v).getTime() / 1000 : Number(v) / 1000);
    const inRange = (t) => t >= eqGeom.tsMin && t <= eqGeom.tsMax;
    const closest = (t) => {
      let best = visibleEquity[0], bd = Infinity;
      for (const p of visibleEquity) {
        const diff = Math.abs(p[0] - t);
        if (diff < bd) { bd = diff; best = p; }
      }
      return best;
    };
    for (const o of orders) {
      const t = fromDate(o.created_at);
      if (!inRange(t)) continue;
      const near = closest(t);
      out.push({
        x: eqGeom.toX(near[0]),
        y: eqGeom.toY(near[1]),
        side: o.side,
        symbol: o.symbol,
        qty: o.qty,
        price: o.price,
        ts: o.created_at,
      });
    }
    for (const b of boosts) {
      const t = fromDate(b.created_at);
      if (!inRange(t)) continue;
      const near = closest(t);
      out.push({
        x: eqGeom.toX(near[0]),
        y: eqGeom.toY(near[1]) - 10,
        side: "boost",
        bonus: b.bonus,
        ts: b.created_at,
      });
    }
    return out;
  }, [eqGeom, orders, boosts, visibleEquity]);

  const currentEquity = stats?.last ?? liveEquity;

  return (
    <div className="mt-4 border border-gold/25 bg-surface p-6" data-testid="equity-chart">
      <div className="mb-5 flex flex-wrap items-baseline justify-between gap-4">
        <div>
          <p className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-gold">
            <Activity className="h-3 w-3" /> Portfolio equity · live
          </p>
          <p
            data-testid="equity-current"
            className={`mt-2 font-serif text-4xl font-light tracking-tight transition-colors duration-150 ${
              flash === "up" ? "text-[#00FF41]" : flash === "down" ? "text-[#FF003C]" : "text-white"
            }`}
          >
            ${fmt(currentEquity)}
          </p>
          {stats && (
            <p className="mt-1 flex items-baseline gap-2 text-[11px]">
              <span className={stats.up ? "text-[#00FF41]" : "text-[#FF003C]"}>
                {stats.up ? "▲" : "▼"} ${fmt(Math.abs(stats.pnl))}
              </span>
              <span className="text-white/40">
                ({stats.up ? "+" : "−"}{fmt(Math.abs(stats.pct), 2)}%) · {activeTf.l}
              </span>
              {btcGeom && (
                <span
                  data-testid="equity-vs-btc"
                  className={`ml-2 rounded-full border px-2 py-0.5 text-[9px] uppercase tracking-[0.15em] ${
                    stats.pct >= btcGeom.btcPct
                      ? "border-[#00FF41]/40 text-[#00FF41]"
                      : "border-[#FF003C]/40 text-[#FF003C]"
                  }`}
                >
                  {stats.pct >= btcGeom.btcPct ? "beating" : "trailing"} BTC{" "}
                  ({btcGeom.btcPct >= 0 ? "+" : "−"}{fmt(Math.abs(btcGeom.btcPct), 2)}%)
                </span>
              )}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#00FF41] opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-[#00FF41]" />
          </span>
          <span className="text-[10px] uppercase tracking-[0.28em] text-[#00FF41]">Live · 500 ms</span>
        </div>
      </div>

      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-1.5" data-testid="equity-tf-toggle">
          {TIMEFRAMES.map((t) => (
            <button
              key={t.k}
              data-testid={`equity-tf-${t.k}`}
              onClick={() => setTf(t.k)}
              className={`rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.2em] transition-colors duration-200 ${
                tf === t.k ? "bg-gold text-ink" : "border border-white/15 text-white/50 hover:border-gold hover:text-gold"
              }`}
            >
              {t.l}
            </button>
          ))}
        </div>
        <button
          data-testid="equity-compare-toggle"
          onClick={() => setShowCompare((s) => !s)}
          className={`rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.2em] transition-colors duration-200 ${
            showCompare ? "border border-[#F7931A]/50 text-[#F7931A]" : "border border-white/10 text-white/40 hover:border-[#F7931A] hover:text-[#F7931A]"
          }`}
        >
          BTC benchmark {showCompare ? "on" : "off"}
        </button>
      </div>

      {!eqGeom ? (
        <p className="py-8 text-center text-xs text-white/30">
          Your equity curve builds itself as the terminal ticks.
        </p>
      ) : (
        <svg viewBox={`0 0 ${W} ${H}`} className="h-52 w-full">
          <defs>
            <linearGradient id="equity-fill" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%"   stopColor={stats.up ? "#00FF41" : "#FF003C"} stopOpacity="0.28" />
              <stop offset="100%" stopColor={stats.up ? "#00FF41" : "#FF003C"} stopOpacity="0" />
            </linearGradient>
            <filter id="equity-glow" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="2.5" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          {[0.25, 0.5, 0.75].map((f) => (
            <line key={f} x1="0" x2={W} y1={H * f} y2={H * f} stroke="rgba(255,255,255,0.05)" strokeDasharray="2 4" />
          ))}
          {/* BTC benchmark (behind equity) */}
          {showCompare && btcGeom && (
            <path
              d={btcGeom.d}
              fill="none"
              stroke="#F7931A"
              strokeWidth="1.1"
              strokeOpacity="0.55"
              strokeDasharray="3 3"
            />
          )}
          {/* Equity fill + line */}
          <path d={`${eqGeom.d} L${W} ${H} L0 ${H} Z`} fill="url(#equity-fill)" />
          <path
            d={eqGeom.d}
            fill="none"
            stroke={stats.up ? "#00FF41" : "#FF003C"}
            strokeWidth="1.6"
            strokeLinejoin="round"
            filter="url(#equity-glow)"
          />
          {/* Trade markers */}
          {markers.map((m, i) => (
            <TradeMarker key={i} m={m} />
          ))}
          {/* Leading dot */}
          <circle
            cx={eqGeom.lx}
            cy={eqGeom.ly}
            r="3.5"
            fill={stats.up ? "#00FF41" : "#FF003C"}
            filter="url(#equity-glow)"
          >
            <animate attributeName="r" values="3.5;5.5;3.5" dur="1.4s" repeatCount="indefinite" />
          </circle>
        </svg>
      )}

      {/* Legend */}
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] uppercase tracking-[0.2em] text-white/40">
        <LegendDot color="#00FF41" label="Equity" />
        {showCompare && <LegendDot color="#F7931A" label="BTC" dashed />}
        <span className="flex items-center gap-1"><span className="text-[#00FF41]">▲</span> Buy</span>
        <span className="flex items-center gap-1"><span className="text-[#FF003C]">▼</span> Sell</span>
        <span className="flex items-center gap-1"><span className="text-gold">★</span> Boost</span>
      </div>

      {stats && (
        <div className="mt-3 grid grid-cols-2 gap-3 border-t border-white/5 pt-3 sm:grid-cols-4">
          <Stat label="Day P&L" value={`${(dayPnl ?? 0) >= 0 ? "+" : "−"}$${fmt(Math.abs(dayPnl ?? 0))}`}
                tone={(dayPnl ?? 0) >= 0 ? "up" : "down"} testid="equity-stat-daypnl" />
          <Stat label={`Peak (${activeTf.l})`} value={`$${fmt(stats.max)}`} testid="equity-stat-peak" />
          <Stat label={`Trough (${activeTf.l})`} value={`$${fmt(stats.min)}`} testid="equity-stat-trough" />
          <Stat label="Drawdown" value={`${fmt(stats.drawdown, 2)}%`}
                tone={stats.drawdown > 0 ? "down" : "neutral"} testid="equity-stat-dd" />
        </div>
      )}
    </div>
  );
}

function TradeMarker({ m }) {
  if (m.side === "boost") {
    return (
      <g data-testid={`equity-marker-boost`}>
        <text x={m.x} y={m.y} textAnchor="middle" fontSize="10" fill="#D4AF37">★</text>
        <title>Boost · +${Number(m.bonus || 0).toFixed(2)} · {new Date(m.ts).toLocaleString()}</title>
      </g>
    );
  }
  const buy = m.side === "buy";
  const color = buy ? "#00FF41" : "#FF003C";
  const tri = buy
    ? `${m.x - 4},${m.y + 8} ${m.x + 4},${m.y + 8} ${m.x},${m.y + 1}`  // ▲
    : `${m.x - 4},${m.y - 8} ${m.x + 4},${m.y - 8} ${m.x},${m.y - 1}`; // ▼
  return (
    <g data-testid={`equity-marker-${m.side}`}>
      <polygon points={tri} fill={color} opacity="0.9" />
      <title>
        {buy ? "Buy" : "Sell"} · {m.symbol} · {m.qty} @ ${Number(m.price || 0).toFixed(2)} · {new Date(m.ts).toLocaleString()}
      </title>
    </g>
  );
}

function LegendDot({ color, label, dashed = false }) {
  return (
    <span className="flex items-center gap-1.5">
      <span
        className="inline-block h-[2px] w-4"
        style={{
          background: dashed
            ? `repeating-linear-gradient(to right, ${color} 0 3px, transparent 3px 6px)`
            : color,
        }}
      />
      {label}
    </span>
  );
}

function Stat({ label, value, tone = "neutral", testid }) {
  const cls =
    tone === "up" ? "text-[#00FF41]"
    : tone === "down" ? "text-[#FF003C]"
    : "text-white";
  return (
    <div>
      <p className="text-[9px] uppercase tracking-[0.25em] text-white/40">{label}</p>
      <p data-testid={testid} className={`mt-1 font-tabular text-sm ${cls}`}>
        {value}
      </p>
    </div>
  );
}
